import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'app-quiz-header',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent],
  templateUrl: './quiz-header.component.html',
  styleUrl: './quiz-header.component.css',
})
export class QuizHeaderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() customClasses: string | string[] = '';
  @Input() iconStart: string = '';
  @Input() iconEnd: string = '';
  @Input() progressBar: boolean = false;
  @Input() progressValue: number = 0;
  @Input() quizButton: boolean = false;
  /**
   * Temps à afficher, en secondes : le temps restant pendant la passation, la
   * durée de la copie sur un résultat. Sans valeur, aucun minuteur.
   *
   * <p>La valeur par défaut était de 50 minutes, pensée pour la passation.
   * L'écran de résultat et le détail d'une copie côté professeur ne passaient
   * aucun temps : ils affichaient ce 50:00 quelle que soit la durée réelle
   * (SKILL2ERA-90).</p>
   */
  @Input() totalSeconds: number | null = null;
  @Input() questionTimeSeconds: number = 0;
  @Input() showQuestionTimer: boolean = false;
  /** À désactiver pour un test sans limite de temps (00:00 figé sinon). */
  @Input() showTestTimer: boolean = true;
  /**
   * Couleurs d'alerte des dernières minutes. Elles disent qu'un temps
   * s'épuise : à couper pour une durée écoulée, qu'une copie de moins d'une
   * minute afficherait sinon en rouge.
   */
  @Input() timerAlerts: boolean = true;

  testTimerDisplay: string = '';
  questionTimerDisplay: string = '00:00';
  isTimerWarning: boolean = false;
  isTimerDanger: boolean = false;
  isQuestionTimerWarning: boolean = false;
  isQuestionTimerDanger: boolean = false;

  ngOnInit(): void {
    this.updateTimerDisplays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['totalSeconds'] || changes['questionTimeSeconds']) {
      this.updateTimerDisplays();
    }
  }

  ngOnDestroy(): void {
    // Component cleanup handled by parent
  }

  private updateTimerDisplays(): void {
    // Update test timer
    const total = this.totalSeconds ?? 0;
    const testMinutes = Math.floor(total / 60);
    const testSeconds = total % 60;
    this.testTimerDisplay = `${this.pad(testMinutes)}:${this.pad(testSeconds)}`;

    // Update question timer
    if (this.showQuestionTimer && this.questionTimeSeconds >= 0) {
      const qMinutes = Math.floor(this.questionTimeSeconds / 60);
      const qSeconds = this.questionTimeSeconds % 60;
      this.questionTimerDisplay = `${this.pad(qMinutes)}:${this.pad(qSeconds)}`;
    }

    // Update warning states for test timer
    this.isTimerWarning = this.timerAlerts && total <= 300 && total > 60; // 5 minutes
    this.isTimerDanger = this.timerAlerts && total <= 60; // 1 minute

    // Update warning states for question timer
    if (this.showQuestionTimer) {
      this.isQuestionTimerWarning =
        this.questionTimeSeconds <= 30 && this.questionTimeSeconds > 10;
      this.isQuestionTimerDanger = this.questionTimeSeconds <= 10;
    }
  }

  private pad(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  get timerClass(): string {
    if (this.isTimerDanger) return 'text-danger timer-danger';
    if (this.isTimerWarning) return 'text-warning timer-warning';
    return 'text-primary';
  }

  get questionTimerClass(): string {
    if (this.isQuestionTimerDanger) return 'text-danger timer-danger';
    if (this.isQuestionTimerWarning) return 'text-warning';
    return 'text-info';
  }

  get progressBarClass(): string {
    if (this.progressValue >= 75) return 'bg-success';
    if (this.progressValue >= 50) return 'bg-info';
    if (this.progressValue >= 25) return 'bg-warning';
    return 'bg-danger';
  }
}
